import { createAsyncThunk, createSlice, isRejectedWithValue } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { appcenterFirestore } from '../../services/firebase';

export interface AuthState {
	isLoggingIn: boolean;
	isLoggedIn: boolean;
	isTaugorCompany: boolean;
	user: AuthUser | null;
	company: AuthCompany | null;
}

export interface AuthPayload {
	user: AuthUser;
	company: AuthCompany;
}

export interface AuthUser {
	uid: string;
	cpf: string;
	name: string;
	email: string;
	phoneArea: string;
	phone: string;
	hasLicense: Record<string, boolean>;
	superadmin: boolean;
}

export interface AuthCompany {
	uid: string;
	cnpj: string;
	name: string;
	fantasyName: string;
	isFranchisee: boolean;
}

const initialState: AuthState = {
	isLoggingIn: true,
	isLoggedIn: false,
	isTaugorCompany: false,
	user: {
		uid: '',
		cpf: '',
		name: '',
		email: '',
		phoneArea: '',
		phone: '',
		hasLicense: {},
		superadmin: false,
	},
	company: {
		uid: '',
		cnpj: '',
		name: '',
		fantasyName: '',
		isFranchisee: false,
	},
};

export const updatePhoneFromAppcenter = createAsyncThunk(
	'auth/updatePhoneFromAppcenter',
	async (
		{ uid, phoneArea, phone }: { uid: string; phoneArea: string; phone: string },
		thunkApi
	) => {
		try {
			const userRef = doc(appcenterFirestore, 'user', uid);
			const userSnap = await getDoc(userRef);

			if (!userSnap.exists()) {
				return thunkApi.rejectWithValue('User not found');
			}
			await updateDoc(userRef, { phoneArea, phone });

			return { uid, phoneArea, phone };
		} catch (error) {
			return thunkApi.rejectWithValue(error);
		}
	}
);

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		login: (state, { payload }: PayloadAction<AuthPayload>) => {
			state.isLoggingIn = false;
			state.isLoggedIn = true;
			state.user = payload.user;
			state.company = payload.company;
			state.isTaugorCompany = payload.company.uid === 'UP4NokWIruNoJuDFj51f';
		},

		logout: (state) => {
			state.isLoggingIn = false;
			state.isLoggedIn = false;
			state.user = null;
			state.company = null;
			state.isTaugorCompany = false;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			updatePhoneFromAppcenter.fulfilled,
			(
				state,
				{ payload }: { payload: boolean | { uid: string; phoneArea: string; phone: string } }
			) => {
				if (state.user && typeof payload === 'object') {
					state.user.phone = payload.phone;
					(state.user as any).phoneArea = payload.phoneArea;
				}
			}
		);
	},
});

export const useAuth = () => useSelector<RootState, AuthState>((state) => state.auth);
